import { pushWithRegion } from 'actions/NavigationActions'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { SearchMenuStates } from 'components/Search/type'
import { INTL_DAY_MONTH_NUMERIC, INTL_SHORT_TIME_FORMAT_AM_PM, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchStateActions, type AirportTransferJourneyType, type GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import { useAppDispatch } from 'hooks/reduxHooks'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import {
  getAirportTransferLocationId,
  getAirportTransferLocationCoordinates,
  getAirportTransferLocationName,
} from 'lib/airportTransfer/airportTransferUtils'
import moment from 'moment'
import qs from 'qs'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import type { Dispatch, FormEvent } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { getQueryParam } from 'lib/url/searchUrlUtils'

interface Options {
  searchDispatch?: Dispatch<GlobalSearchAction>
  searchState?: GlobalSearchState
}

export function doesPickUpDateTimeInvalidateReturn(
  date: moment.Moment | undefined,
  time: string | undefined,
  checkoutDate: moment.Moment | undefined,
  dropOffTime: string | undefined,
): boolean {
  if (!date || !checkoutDate) {
    return false
  }

  return (
    moment(date).isAfter(checkoutDate, 'day') ||
    (
      moment(date).isSame(checkoutDate, 'day') &&
      !!time &&
      !!dropOffTime &&
      moment(time, 'HH:mm').isAfter(moment(dropOffTime, 'HH:mm'))
    )
  )
}

export function requestAirportTransferAddReturn() {
  window.dispatchEvent(new CustomEvent('cars-airport-transfer-add-return'))
}

function useAirportTransferSearchControls(options: Options = {}) {
  const intl = useIntl()

  const dispatch = useAppDispatch()
  const { search: locationSearch, pathname } = useLocation()
  const contextSearchState = useContext(GlobalSearchStateContext)
  const contextSearchDispatch = useContext(GlobalSearchDispatchContext)
  const searchState = options.searchState ?? contextSearchState
  const searchDispatch = options.searchDispatch ?? contextSearchDispatch
  const { checkinDate, pickUpTime, checkoutDate, dropOffTime, searchItem, secondarySearchItem, activeMenu, airportTransferJourneyType, airportTransferLuggage, airportTransferPassengers, isAirportTransferReturnPromptPending } = searchState

  const onSwapLocations = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem: secondarySearchItem })
    searchDispatch({ type: GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM, searchItem })
  }, [searchDispatch, searchItem, secondarySearchItem])

  const onFromLocationSelect = useCallback((nextSearchItem: App.SearchItem) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem: nextSearchItem })
  }, [searchDispatch])

  const onToLocationSelect = useCallback((nextSearchItem: App.SearchItem) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM, searchItem: nextSearchItem })
  }, [searchDispatch])

  const openJourneyTypeSelection = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.AirportTransferJourneyTypeSelection })
  }, [searchDispatch])

  const closeJourneyTypeSelection = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [searchDispatch])

  const onJourneyTypeSelect = useCallback((journeyType: AirportTransferJourneyType) => {
    searchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_RETURN_PROMPT_PENDING,
      isPending: false,
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_JOURNEY_TYPE, journeyType })
    if (journeyType === 'return' && !dropOffTime) {
      searchDispatch({ type: GlobalSearchStateActions.SET_RETURN_TIME, dropOffTime: '09:00' })
    }
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [dropOffTime, searchDispatch])

  const onSearch = useCallback((
    event?: FormEvent<HTMLFormElement>,
    overrides?: {
      journeyType?: AirportTransferJourneyType
      checkoutDate?: moment.Moment
      dropOffTime?: string
    },
  ) => {
    event?.preventDefault()
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })

    if (event) {
      trackClientEvent({
        category: 'airport_transfer',
        subject: 'filters',
        action: 'header_applied',
        type: 'interaction',
      })
    }

    const nextJourneyType = overrides?.journeyType ?? airportTransferJourneyType
    const nextCheckoutDate = overrides?.checkoutDate ?? checkoutDate
    const nextDropOffTime = overrides?.dropOffTime ?? dropOffTime

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        ...searchState,
        airportTransferJourneyType: nextJourneyType,
        checkoutDate: nextCheckoutDate,
        dropOffTime: nextDropOffTime,
        searchVerticals: new Set(['cars']),
      }),
    }))

    const pickupCoordinates = getAirportTransferLocationCoordinates(searchItem)
    const dropoffCoordinates = getAirportTransferLocationCoordinates(secondarySearchItem)
    const query = qs.stringify({
      ...(pathname.endsWith('/search/transfer') && ['cart', 'checkout'].includes(getQueryParam(locationSearch, 'transferQuoteRecovery') ?? '') && {
        transferQuoteRecovery: getQueryParam(locationSearch, 'transferQuoteRecovery'),
        transferQuoteCartId: getQueryParam(locationSearch, 'transferQuoteCartId'),
        transferQuoteCheckoutCartId: getQueryParam(locationSearch, 'transferQuoteCheckoutCartId'),
        transferQuoteJourneyId: getQueryParam(locationSearch, 'transferQuoteJourneyId'),
      }),
      pickUpName: getAirportTransferLocationName(searchItem),
      pickUpId: getAirportTransferLocationId(searchItem),
      pickUpLat: pickupCoordinates?.lat,
      pickUpLng: pickupCoordinates?.lng,
      pickUpSearchType: searchItem?.searchType,
      dropOffName: getAirportTransferLocationName(secondarySearchItem),
      dropOffId: getAirportTransferLocationId(secondarySearchItem),
      dropOffLat: dropoffCoordinates?.lat,
      dropOffLng: dropoffCoordinates?.lng,
      dropOffSearchType: secondarySearchItem?.searchType,
      pickUpDate: checkinDate?.format(ISO_DATE_FORMAT),
      pickUpTime,
      journeyType: nextJourneyType,
      ...(nextJourneyType === 'return' && {
        returnDate: nextCheckoutDate?.format(ISO_DATE_FORMAT),
        returnTime: nextDropOffTime,
      }),
      passengers: airportTransferPassengers,
      luggage: airportTransferLuggage,
    })

    dispatch(pushWithRegion('/search/transfer', query))
  }, [
    airportTransferJourneyType,
    airportTransferLuggage,
    airportTransferPassengers,
    checkinDate,
    checkoutDate,
    dispatch,
    dropOffTime,
    locationSearch,
    pathname,
    pickUpTime,
    searchDispatch,
    searchState,
    searchItem,
    secondarySearchItem,
  ])

  const openPickUpDatePicker = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.AirportTransferPickUpDatePicker })
  }, [searchDispatch])

  const openReturnDatePicker = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.AirportTransferReturnDatePicker })
  }, [searchDispatch])

  const onPickUpDateChange = useCallback(({ date, time }: { date?: moment.Moment, time?: string }) => {
    const isAfterReturnDate = doesPickUpDateTimeInvalidateReturn(
      date,
      time,
      checkoutDate,
      dropOffTime,
    )

    if (date) searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: moment(date) })
    if (time) searchDispatch({ type: GlobalSearchStateActions.SET_PICKUP_TIME, pickupTime: time })
    if (isAfterReturnDate) {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
      searchDispatch({
        type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_JOURNEY_TYPE,
        journeyType: 'one-way',
      })
    }

    if (
      activeMenu === SearchMenuStates.AirportTransferPickUpDatePicker &&
      airportTransferJourneyType === 'return' &&
      !checkoutDate &&
      !isAfterReturnDate
    ) {
      openReturnDatePicker()
    }
  }, [activeMenu, airportTransferJourneyType, checkoutDate, dropOffTime, openReturnDatePicker, searchDispatch])

  const startReturnSearchFromPrompt = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_JOURNEY_TYPE, journeyType: 'return' })
    if (!dropOffTime) {
      searchDispatch({ type: GlobalSearchStateActions.SET_RETURN_TIME, dropOffTime: '09:00' })
    }
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.AirportTransferReturnDatePicker })
  }, [dropOffTime, searchDispatch])

  const onAddReturnFromPrompt = useCallback(() => {
    searchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_RETURN_PROMPT_PENDING,
      isPending: true,
    })
    startReturnSearchFromPrompt()
  }, [searchDispatch, startReturnSearchFromPrompt])

  useEffect(() => {
    window.addEventListener('cars-airport-transfer-add-return', onAddReturnFromPrompt)
    return () => {
      window.removeEventListener('cars-airport-transfer-add-return', onAddReturnFromPrompt)
    }
  }, [onAddReturnFromPrompt])

  const onReturnDateChange = useCallback(({ date, time }: { date?: moment.Moment, time?: string }) => {
    if (date) {
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: moment(date) })
      searchDispatch({ type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_JOURNEY_TYPE, journeyType: 'return' })
    }
    if (time) searchDispatch({ type: GlobalSearchStateActions.SET_RETURN_TIME, dropOffTime: time })

    if (!isAirportTransferReturnPromptPending || !date) {
      return
    }

    const nextDropOffTime = time || dropOffTime || '09:00'
    searchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_RETURN_PROMPT_PENDING,
      isPending: false,
    })
    onSearch(undefined, {
      journeyType: 'return',
      checkoutDate: date,
      dropOffTime: nextDropOffTime,
    })
  }, [dropOffTime, isAirportTransferReturnPromptPending, onSearch, searchDispatch])

  const openLocations = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Locations })
  }, [searchDispatch])

  const openPaxAndLuggageSelection = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.AirportTransferPaxAndLuggageSelection })
  }, [searchDispatch])

  const closeMenu = useCallback((desiredMenu: SearchMenuStates) => {
    if (desiredMenu === activeMenu) searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [searchDispatch, activeMenu])

  const revertReturnPromptToOneWay = useCallback(() => {
    if (!isAirportTransferReturnPromptPending) return

    searchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_RETURN_PROMPT_PENDING,
      isPending: false,
    })
    searchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_JOURNEY_TYPE,
      journeyType: 'one-way',
    })
  }, [isAirportTransferReturnPromptPending, searchDispatch])

  const closeLocations = useCallback(() => closeMenu(SearchMenuStates.Locations), [closeMenu])
  const closePickUpDatePicker = useCallback(() => closeMenu(SearchMenuStates.AirportTransferPickUpDatePicker), [closeMenu])
  const closeReturnDatePicker = useCallback(() => {
    revertReturnPromptToOneWay()
    closeMenu(SearchMenuStates.AirportTransferReturnDatePicker)
  }, [closeMenu, revertReturnPromptToOneWay])
  const closePaxAndLuggageSelection = useCallback(() => closeMenu(SearchMenuStates.AirportTransferPaxAndLuggageSelection), [closeMenu])

  useEffect(() => {
    if (airportTransferJourneyType === 'one-way' && (checkoutDate || dropOffTime)) {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
      searchDispatch({ type: GlobalSearchStateActions.UNSET_RETURN_TIME })
    }
  }, [searchDispatch, airportTransferJourneyType, checkoutDate, dropOffTime])

  const { pickUpDateTimeLabel, returnDateTimeLabel } = useMemo(() => {
    let pickUpLabel = ''
    let returnLabel = ''

    if (checkinDate && pickUpTime) {
      const formattedDate = intlDateFormatter(moment(checkinDate), INTL_DAY_MONTH_NUMERIC, intl.locale)
      const localizedTime = intlDateFormatter(moment(pickUpTime, 'HH:mm'), INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)
      pickUpLabel = `${formattedDate} · ${localizedTime}`
    }

    if (checkoutDate && dropOffTime) {
      const formattedDate = intlDateFormatter(moment(checkoutDate), INTL_DAY_MONTH_NUMERIC, intl.locale)
      const localizedTime = intlDateFormatter(moment(dropOffTime, 'HH:mm'), INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)
      returnLabel = `${formattedDate} · ${localizedTime}`
    }

    return {
      pickUpDateTimeLabel: pickUpLabel,
      returnDateTimeLabel: returnLabel,
    }
  }, [checkinDate, pickUpTime, checkoutDate, dropOffTime, intl])

  return {
    locationFromLabel: getAirportTransferLocationName(searchItem) ?? '',
    locationToLabel: getAirportTransferLocationName(secondarySearchItem) ?? '',
    pickUpDateTimeLabel,
    returnDateTimeLabel,
    onSwapLocations,
    onFromLocationSelect,
    onToLocationSelect,
    openJourneyTypeSelection,
    closeJourneyTypeSelection,
    onJourneyTypeSelect,
    onSearch,
    openPickUpDatePicker,
    onPickUpDateChange,
    openReturnDatePicker,
    onAddReturnFromPrompt,
    onReturnDateChange,
    openLocations,
    openPaxAndLuggageSelection,
    closeLocations,
    closePickUpDatePicker,
    closeReturnDatePicker,
    closePaxAndLuggageSelection,
    isAirportTransferReturnPromptPending,
  }
}

export default useAirportTransferSearchControls
